import type { InboxRequest } from '../../shared/signaling.js';
import { isValidPin, parseInboxKey } from '../../shared/signaling.js';
import { SignalingConnectionError } from './errors.js';
import { formatPin, normalizePinInput } from './format.js';
import { t } from './i18n/index.js';
import { createInbox, extractPastedCredentials, recoverInboxNumber } from './inbox-api.js';
import type { InboxCredentials } from './inbox-storage.js';
import { forgetInboxCredentials, loadInboxCredentials, saveInboxCredentials } from './inbox-storage.js';
import { renderQrCode } from './qr.js';
import { runReceiveSession } from './receive-session.js';
import { buildInboxLink } from './route.js';
import { SignalingClient, signalingUrl } from './signaling-client.js';
import { SaveCancelledError } from './sinks/file-sink.js';
import { createSink, detectSinkKind } from './sinks/index.js';
import { clear, el } from './ui/dom.js';
import { createFileCard } from './ui/file-card.js';
import { createOutcomeCard } from './ui/outcome-card.js';
import { printPoster } from './ui/poster.js';
import { createHeader, createErrorCard, userFacingError } from './ui/shell.js';
import { createStatusPill } from './ui/status.js';

const RECONNECT_DELAY_MS = 3000;

export type KnownRequest = Readonly<{ roomId: string; unanswered: boolean }>;

/**
 * Which cards to drop after a fresh pending list arrived: unanswered requests the server no longer
 * knows about (the customer left while this page was disconnected). Transfers in progress stay.
 */
export const staleRequestIds = (known: readonly KnownRequest[], pendingIds: ReadonlySet<string>): readonly string[] =>
  known.filter((request) => request.unanswered && !pendingIds.has(request.roomId)).map((request) => request.roomId);

type RequestControls = Readonly<{
  /** Tells the server the owner accepts; resolves once the room is open for the transfer socket. */
  accept: (roomId: string) => Promise<void>;
  decline: (roomId: string) => void;
}>;

/** One incoming request: file details, Accept/Decline, then the transfer's progress and outcome in place. */
const createRequestCard = (request: InboxRequest, controls: RequestControls): HTMLElement => {
  const acceptButton = el('button', { className: 'button', type: 'button', text: t('inbox.request.accept') });
  const declineButton = el('button', { className: 'button button--ghost', type: 'button', text: t('inbox.request.decline') });
  const actions = el('div', { className: 'request__actions' }, [acceptButton, declineButton]);
  const note = el('div', { className: 'share__note' });
  const progressHost = el('div');
  const resultHost = el('div');
  const card = el('section', { className: 'card request' }, [createFileCard(request.file), actions, note, progressHost, resultHost]);

  acceptButton.addEventListener('click', () => {
    acceptButton.disabled = true;
    declineButton.disabled = true;
    // The destination is chosen inside the click so the save dialog counts as a user gesture.
    createSink(detectSinkKind(), request.file)
      .then(async (sink) => {
        actions.remove();
        note.textContent = t('inbox.request.connecting');
        note.className = 'share__note';
        await controls.accept(request.roomId);
        const received = await runReceiveSession(
          { type: 'join', roomId: request.roomId },
          {
            onConnected: () => undefined,
            onChannelOpen: () => undefined,
            chooseSink: () => Promise.resolve(sink),
            onTransferStart: (progress) => {
              note.textContent = '';
              progressHost.replaceChildren(progress.element);
            },
          },
        );
        resultHost.replaceChildren(createOutcomeCard(received, false));
      })
      .catch((error: unknown) => {
        acceptButton.disabled = false;
        declineButton.disabled = false;
        if (error instanceof SaveCancelledError) {
          note.textContent = t('dest.cancelled');
        } else {
          note.textContent = userFacingError(error);
        }
        note.className = 'share__note share__note--warn';
      });
  });
  declineButton.addEventListener('click', () => {
    controls.decline(request.roomId);
    card.remove();
  });
  return card;
};

type ConsoleUi = Readonly<{
  status: ReturnType<typeof createStatusPill>;
  requests: HTMLElement;
  empty: HTMLElement;
  refresh: HTMLButtonElement;
}>;

const createNumberCard = async (credentials: InboxCredentials): Promise<HTMLElement> => {
  const link = buildInboxLink(window.location.origin, credentials.number);
  const canvas = el('canvas', { className: 'share__qr' });
  await renderQrCode(canvas, link);
  const printButton = el('button', { className: 'button button--large', type: 'button', text: t('inbox.print') });
  const printNote = el('div', { className: 'share__note' });
  printButton.addEventListener('click', () => {
    printPoster(credentials.number, link).catch((error: unknown) => {
      printNote.textContent = userFacingError(error);
      printNote.className = 'share__note share__note--warn';
    });
  });
  const forget = el('a', { className: 'link', href: '/inbox', text: t('inbox.forget') });
  forget.addEventListener('click', (event) => {
    event.preventDefault();
    forgetInboxCredentials();
    window.location.reload();
  });
  return el('section', { className: 'card' }, [
    el('div', { className: 'card__title', text: t('inbox.number.title') }),
    el('p', { className: 'card__text', text: t('inbox.number.text') }),
    el('div', { className: 'pin', text: formatPin(credentials.number) }),
    el('div', { className: 'share__qr-wrap' }, [canvas]),
    el('p', { className: 'card__text inbox__link', text: link }),
    printButton,
    printNote,
    el('p', { className: 'card__text save__follow-up' }, [forget]),
  ]);
};

/** Keeps the owner socket alive: authenticates, renders pending requests, reconnects after drops. */
const keepConnected = (credentials: InboxCredentials, ui: ConsoleUi, host: HTMLElement): void => {
  let client: SignalingClient | null = null;
  let stopped = false;
  /** Bumped on every (re)connect so a stale socket's close handler cannot schedule a second reconnect. */
  let generation = 0;
  const requestCards = new Map<string, HTMLElement>();

  const controls: RequestControls = {
    accept: async (roomId) => {
      if (client === null) {
        throw new Error('Not connected to the inbox');
      }
      const accepted = client.waitFor(
        (message): message is { type: 'inbox-accepted'; roomId: string } => message.type === 'inbox-accepted' && message.roomId === roomId,
      );
      client.send({ type: 'inbox-accept', roomId });
      await accepted;
    },
    decline: (roomId) => {
      client?.send({ type: 'inbox-decline', roomId });
      requestCards.delete(roomId);
      ui.empty.hidden = requestCards.size > 0;
    },
  };

  const showRequest = (request: InboxRequest): void => {
    if (requestCards.has(request.roomId)) {
      return;
    }
    const card = createRequestCard(request, controls);
    requestCards.set(request.roomId, card);
    ui.requests.prepend(card);
    ui.empty.hidden = true;
  };

  const isUnanswered = (card: HTMLElement): boolean => card.querySelector('.request__actions') !== null;

  const dropRequest = (roomId: string): void => {
    const card = requestCards.get(roomId);
    if (card === undefined) {
      return;
    }
    // A transfer already in progress keeps its card; only an unanswered request disappears.
    if (isUnanswered(card)) {
      card.remove();
      requestCards.delete(roomId);
    }
    ui.empty.hidden = requestCards.size > 0;
  };

  /** Brings the list in line with the server: adds what is pending, drops unanswered cards that are gone. */
  const reconcile = (pending: readonly InboxRequest[]): void => {
    const known = [...requestCards].map(([roomId, card]) => ({ roomId, unanswered: isUnanswered(card) }));
    for (const roomId of staleRequestIds(known, new Set(pending.map((request) => request.roomId)))) {
      requestCards.get(roomId)?.remove();
      requestCards.delete(roomId);
    }
    pending.forEach(showRequest);
    ui.empty.hidden = requestCards.size > 0;
  };

  const connect = async (): Promise<void> => {
    generation += 1;
    const current = generation;
    ui.status.set('waiting', t('inbox.status.connecting'));
    ui.refresh.disabled = true;
    const next = await SignalingClient.connect(signalingUrl(window.location));
    const ready = next.waitFor(
      (message): message is { type: 'inbox-ready'; inbox: string; pending: readonly InboxRequest[] } => message.type === 'inbox-ready',
    );
    next.send({ type: 'inbox-auth', inbox: credentials.number, key: credentials.key });
    const { pending } = await ready;
    client = next;
    saveInboxCredentials(credentials);
    ui.status.set('success', t('inbox.status.online'));
    ui.refresh.disabled = false;
    reconcile(pending);
    next.onMessage((message) => {
      if (message.type === 'inbox-request') {
        showRequest(message.request);
      } else if (message.type === 'inbox-request-cancelled') {
        dropRequest(message.roomId);
      }
    });
    next.onClose(() => {
      if (stopped || current !== generation) {
        return;
      }
      client = null;
      ui.status.set('waiting', t('inbox.status.reconnecting'));
      window.setTimeout(() => {
        connect().catch(fail);
      }, RECONNECT_DELAY_MS);
    });
  };

  /** Manual refresh: a fresh socket and a fresh pending list, without waiting for the retry timer. */
  const refresh = (): void => {
    const previous = client;
    client = null;
    connect().catch(fail);
    previous?.close();
  };
  ui.refresh.addEventListener('click', refresh);

  const fail = (error: unknown): void => {
    stopped = true;
    ui.status.set('error', t('status.failed'));
    const unauthorized = error instanceof SignalingConnectionError && error.failure === 'inbox-unauthorized';
    host.append(
      createErrorCard(userFacingError(error), unauthorized ? t('inbox.forget') : t('action.retry'), () => {
        if (unauthorized) {
          forgetInboxCredentials();
        }
        window.location.reload();
      }),
    );
  };

  window.addEventListener('pagehide', () => {
    stopped = true;
  });
  connect().catch(fail);
};

const renderConsole = async (main: HTMLElement, credentials: InboxCredentials): Promise<void> => {
  const ui: ConsoleUi = {
    status: createStatusPill(),
    requests: el('div', { className: 'requests' }),
    empty: el('p', { className: 'card__text', text: t('inbox.requests.empty') }),
    refresh: el('button', { className: 'button button--ghost button--small', type: 'button', text: `↻ ${t('inbox.refresh')}` }),
  };
  const errorHost = el('div');
  // Two columns on wide screens: the live request list on the left, the number/QR card on the right.
  main.classList.add('page--wide');
  main.append(
    el('div', { className: 'console' }, [
      el('div', { className: 'console__main' }, [
        ui.status.element,
        el('section', { className: 'card' }, [
          el('div', { className: 'card__head' }, [el('div', { className: 'card__title', text: t('inbox.requests.title') }), ui.refresh]),
          ui.empty,
        ]),
        ui.requests,
        errorHost,
      ]),
      el('aside', { className: 'console__aside' }, [await createNumberCard(credentials)]),
    ]),
  );
  keepConnected(credentials, ui, errorHost);
};

/** Text the owner keeps: both halves, because either one alone is not enough to reconnect. */
export const credentialsBlock = (credentials: InboxCredentials): string =>
  `${t('inbox.connect.number')}: ${formatPin(credentials.number)}\n${t('inbox.connect.key')}: ${credentials.key}`;

/** Shown exactly once after creation; the key never appears again. */
const createKeyCard = (credentials: InboxCredentials, onDone: () => void): HTMLElement => {
  const numberBox = el('div', { className: 'pin', text: formatPin(credentials.number) });
  const keyBox = el('div', { className: 'key', text: credentials.key });
  const copyButton = el('button', { className: 'button button--ghost', type: 'button', text: t('inbox.key.copy') });
  const doneButton = el('button', { className: 'button', type: 'button', text: t('inbox.key.done') });
  const note = el('div', { className: 'share__note' });
  copyButton.addEventListener('click', () => {
    navigator.clipboard
      .writeText(credentialsBlock(credentials))
      .then(() => {
        note.textContent = t('inbox.key.copied');
        note.className = 'share__note share__note--ok';
      })
      .catch((error: unknown) => {
        note.textContent = userFacingError(error);
        note.className = 'share__note share__note--warn';
      });
  });
  doneButton.addEventListener('click', onDone);
  return el('section', { className: 'card card--key' }, [
    el('div', { className: 'card__title', text: t('inbox.key.title') }),
    el('p', { className: 'card__text', text: t('inbox.key.text') }),
    el('div', { className: 'key__label', text: t('inbox.connect.number') }),
    numberBox,
    el('div', { className: 'key__label', text: t('inbox.connect.key') }),
    keyBox,
    el('div', { className: 'request__actions' }, [copyButton, doneButton]),
    note,
  ]);
};

const renderOnboarding = (main: HTMLElement): void => {
  const note = el('div', { className: 'share__note' });
  const createButton = el('button', { className: 'button button--large', type: 'button', text: t('inbox.create.button') });
  const createCard = el('section', { className: 'card' }, [
    el('div', { className: 'card__title', text: t('inbox.create.title') }),
    el('p', { className: 'card__text', text: t('inbox.create.text') }),
    createButton,
    note,
  ]);

  const numberInput = el('input', { className: 'pin-entry__input', type: 'text', ariaLabel: t('inbox.connect.numberOptional') });
  numberInput.inputMode = 'numeric';
  numberInput.placeholder = t('inbox.connect.numberOptional');
  numberInput.addEventListener('input', () => {
    numberInput.value = formatPin(normalizePinInput(numberInput.value).slice(0, 9));
  });
  const keyInput = el('input', { className: 'share__link', type: 'text', ariaLabel: t('inbox.connect.key') });
  keyInput.placeholder = t('inbox.connect.key');
  keyInput.autocomplete = 'off';
  // Pasting the whole saved block fills both fields.
  keyInput.addEventListener('input', () => {
    const pasted = extractPastedCredentials(keyInput.value);
    if (pasted.key !== null && pasted.key !== keyInput.value) {
      keyInput.value = pasted.key;
    }
    if (pasted.number !== null && normalizePinInput(numberInput.value).length === 0) {
      numberInput.value = formatPin(pasted.number);
    }
  });
  const connectButton = el('button', { className: 'button', type: 'submit', text: t('inbox.connect.button') });
  const connectNote = el('div', { className: 'share__note' });
  const form = el('form', { className: 'inbox-connect' }, [numberInput, keyInput, connectButton]);
  const openConsole = (credentials: InboxCredentials): void => {
    clear(main);
    main.append(createHeader(t('inbox.tagline')));
    renderConsole(main, credentials).catch((error: unknown) => {
      main.append(createErrorCard(userFacingError(error), t('action.retry'), () => window.location.reload()));
    });
  };
  const warn = (text: string): void => {
    connectNote.textContent = text;
    connectNote.className = 'share__note share__note--warn';
  };
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const typedNumber = normalizePinInput(numberInput.value);
    let key: string;
    try {
      key = parseInboxKey(keyInput.value.trim());
    } catch (error) {
      warn(userFacingError(error));
      return;
    }
    if (typedNumber.length > 0 && !isValidPin(typedNumber)) {
      warn(t('pin.incomplete', { count: 9 }));
      return;
    }
    connectButton.disabled = true;
    // The number is optional: the server can find it from the key.
    const number = typedNumber.length > 0 ? Promise.resolve(typedNumber) : recoverInboxNumber(key);
    number
      .then((resolved) => openConsole({ number: resolved, key }))
      .catch((error: unknown) => {
        connectButton.disabled = false;
        warn(userFacingError(error));
      });
  });
  const connectCard = el('section', { className: 'card' }, [
    el('div', { className: 'card__title', text: t('inbox.connect.title') }),
    el('p', { className: 'card__text', text: t('inbox.connect.text') }),
    form,
    connectNote,
  ]);

  createButton.addEventListener('click', () => {
    createButton.disabled = true;
    createInbox()
      .then((credentials) => {
        saveInboxCredentials(credentials);
        createCard.replaceWith(
          createKeyCard(credentials, () => {
            clear(main);
            main.append(createHeader(t('inbox.tagline')));
            renderConsole(main, credentials).catch((error: unknown) => {
              main.append(createErrorCard(userFacingError(error), t('action.retry'), () => window.location.reload()));
            });
          }),
        );
        connectCard.remove();
      })
      .catch((error: unknown) => {
        createButton.disabled = false;
        note.textContent = userFacingError(error);
        note.className = 'share__note share__note--warn';
      });
  });

  main.append(createCard, connectCard);
};

export const renderInboxPage = (root: HTMLElement): void => {
  clear(root);
  const main = el('main', { className: 'page' });
  main.append(createHeader(t('inbox.tagline')));
  root.append(main);
  const credentials = loadInboxCredentials();
  if (credentials === null) {
    renderOnboarding(main);
    return;
  }
  renderConsole(main, credentials).catch((error: unknown) => {
    main.append(createErrorCard(userFacingError(error), t('action.retry'), () => window.location.reload()));
  });
};
